import { useEffect, useRef, useMemo } from 'react'
import { EditorState } from '@codemirror/state'
import { EditorView, keymap, placeholder } from '@codemirror/view'
import { defaultKeymap } from '@codemirror/commands'
import { autocompletion, type CompletionContext, type CompletionResult } from '@codemirror/autocomplete'
import { getFormulaError, buildCompletions } from './formulaSupport'
import type { CatalogField } from './types'

interface Props {
  value: string
  onChange: (value: string) => void
  fields: CatalogField[]
}

export default function FormulaEditor({ value, onChange, fields }: Props) {
  const host = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const completions = useMemo(() => buildCompletions(fields), [fields])
  const completionsRef = useRef(completions)
  completionsRef.current = completions

  useEffect(() => {
    if (!host.current) return
    const complete = (ctx: CompletionContext): CompletionResult | null => {
      const word = ctx.matchBefore(/[A-Za-z_][A-Za-z0-9_]*/)
      if (!word || (word.from === word.to && !ctx.explicit)) return null
      return { from: word.from, options: completionsRef.current }
    }
    const view = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: value,
        extensions: [
          keymap.of(defaultKeymap),
          placeholder('ex : spend / conversions'),
          autocompletion({ override: [complete] }),
          EditorView.updateListener.of((u) => {
            if (u.docChanged) onChangeRef.current(u.state.doc.toString())
          }),
          EditorView.theme({ '&': { fontSize: '13px', border: '1px solid #E2E4ED', borderRadius: '12px' }, '.cm-content': { fontFamily: 'ui-monospace, monospace', padding: '10px' } }),
        ],
      }),
    })
    viewRef.current = view
    return () => { view.destroy(); viewRef.current = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const view = viewRef.current
    if (view && value !== view.state.doc.toString()) {
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: value } })
    }
  }, [value])

  const error = getFormulaError(value)

  return (
    <div>
      <div ref={host} data-testid="formula-editor" />
      {error && (
        <p data-testid="formula-error" className="flex items-center gap-1.5 text-xs text-red-500 mt-1.5">
          {error.message}
        </p>
      )}
    </div>
  )
}
